import type posthogType from 'posthog-js';
import PriceUtils from '../../main/core/price-utils';
import { v7 as uuidv7 } from 'uuid';

type PH = typeof posthogType;

export type EventKind = 'functional' | 'measurement' | 'marketing' | 'manual_anon';

export type ConsentPSM = {
    preferences: boolean;        // storage tecnico/feature flags
    statistics: boolean;       // analytics anonimi (pageview + autocapture)
    marketing: boolean;         // profili/PII + identify
    allowFeatureFlags?: boolean; // opzionale: abilita cookie/LS anche con solo preferences
    sessionRecording?: boolean;  // opzionale: registra sessioni quando statistics=true
};

declare let core_object: any;

const priceUtils = new PriceUtils();

export type ConsentKind = 'safe' | 'full'

export default class posthogActions{

    private ph: PH;
    private ready = false;
    private queue: Array<{ name: string; props: Record<string, any>; kind?: EventKind }> = [];
    
    private consent: {
        preferences: boolean;
        statistics: boolean;
        marketing: boolean,
        allowFeatureFlags: boolean,
        sessionRecording: boolean
    } = {
        preferences: false,
        statistics: false,
        marketing: false,
        allowFeatureFlags: false,
        sessionRecording: false,
    };

    private readonly purgeOnMemory: boolean;

    // Constructor
    constructor(deps: { ph: PH; ready: Promise<PH> }, options?: { purgeOnMemory?: boolean }) {
        
        this.ph = deps.ph;

        this.purgeOnMemory = options?.purgeOnMemory ?? true;

        // when the bootstrap is ready, unlock the queue
        deps.ready.then(() => {
            this.ready = true;
            this.flush();
        });

    }

    // Get ephemeral SID
    private getEphemeralSid(): string{

        let sid = sessionStorage.getItem('pph_ephemeral_sid');

        if(!sid){
            
            sid = uuidv7();
            sessionStorage.setItem('pph_ephemeral_sid', sid);

        }

        return sid;

    }

    // Is safe
    private isSafe(): boolean {

        return (this.ph as any).config?.persistence === 'memory';

    }

    // Flush
    private flush(): void{

        if(!this.ready || this.queue.length === 0) return;
        
        const pending = [...this.queue];

        this.queue.length = 0;

        for(const item of pending){

            this.capture(item.name, item.props, item.kind || 'measurement');

        }

    }

    // Hard clear Posthog persistence
    private hardClearPosthogPersistence(){
        
        try{

            // 1) LocalStorage & SessionStorage
            for(const store of [localStorage, sessionStorage]){

                for(const k of Object.keys(store)){

                    if(/^ph[_.-]/i.test(k) || /posthog/i.test(k)){

                        store.removeItem(k);

                    }

                }

            }

            // 2) Cookie su più varianti di dominio e path
            const host = location.hostname.replace(/:\d+$/, '');    // rimuovi eventuale :porta
            const parts = host.split('.');
            const domainCandidates = new Set<string | undefined>([
            undefined,
            host,
            '.' + host,
            ...parts.map((_, i) => parts.slice(i).join('.')),          // www.example.com, example.com
            ...parts.map((_, i) => '.' + parts.slice(i).join('.')),    // .www.example.com, .example.com
            ]);

            // Path candidates: '/' e gerarchia dell'URL corrente
            const segs = location.pathname.split('/').filter(Boolean);
            const pathCandidates = new Set<string>(['/']);

            for(let i = 0; i < segs.length; i++){

                pathCandidates.add('/' + segs.slice(0, i + 1).join('/'));

            }

            // Prendi solo i cookie PostHog
            const all = document.cookie.split(';').map(c => c.trim()).filter(Boolean);
            const names = all
            .map(c => c.split('=')[0])
            .filter(n => /^ph/i.test(n) || /posthog/i.test(n));

            for(const name of names){
                
                for(const domain of domainCandidates){

                    for(const path of pathCandidates){

                        document.cookie =
                            `${name}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=${path}` +
                            (domain ? `; Domain=${domain}` : '') + `; SameSite=Lax; Secure`;
                        
                        document.cookie =
                            `${name}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=${path}` +
                            (domain ? `; Domain=${domain}` : '') + `; SameSite=Lax`;
                        
                    }

                }
                
            }

        }catch (e){

            console.warn('hardClearPosthogPersistence failed', e);

        }

    }

    /**
     * Apply banner preferences (PSM) without re-initializing.
     * - Preferences: technical storage only/feature flags (optional)
     * - Statistics: analytics anonimi (pageview + autocapture), NO identify
     * - Marketing: profiles + identification
     */
    applyConsentPSM(c: ConsentPSM): void {

        // Save internal status
        this.consent = {
            preferences: !!c.preferences,
            statistics: !!c.statistics || !!c.marketing, // marketing involves analysis
            marketing: !!c.marketing,
            allowFeatureFlags: !!c.allowFeatureFlags,
            sessionRecording: !!c.sessionRecording,
        };

        const wantsCookies = this.consent.marketing || (!!c.statistics) || (!!c.preferences && !!c.allowFeatureFlags);

        this.ph.set_config({
            // persistence
            persistence: wantsCookies ? 'localStorage+cookie' : 'memory',
            disable_cookie: !wantsCookies,

            // autocapture/pageview: active if statistics or marketing
            autocapture: this.consent.statistics,
            capture_pageview: this.consent.statistics,

            // session recording: only if explicitly requested and statistics enabled
            disable_session_recording: !(!!c.statistics && c.sessionRecording === true),

            // profiles: only with marketing
            person_profiles: this.consent.marketing ? 'always' : 'identified_only',
        } as any);

        // If you opted out of previous tests but now there is consent at the state/marketing level, reinstate it.
        if((this.ph as any).has_opted_out_capturing?.() && this.consent.statistics){

            (this.ph as any).opt_in_capturing?.();

        }

        // If we have switched to SAFE (no cookies) and prudential cleaning is enabled
        const isNowSafe = (this.ph as any).config?.persistence === 'memory';
        if(isNowSafe && this.purgeOnMemory) this.hardClearPosthogPersistence();

        // Pageview after change only if statistics are active
        if(this.consent.statistics){

            this.capture('$pageview', {});

        }

        // console.log('[PH consent]', {
        //     persistence: (this.ph as any).config?.persistence,
        //     distinct_id: this.ph.get_distinct_id(),
        //     autocapture: (this.ph as any).config?.autocapture,
        //     capture_pageview: (this.ph as any).config?.capture_pageview,
        //     disable_session_recording: (this.ph as any).config?.disable_session_recording,
        //     person_profiles: (this.ph as any).config?.person_profiles,
        //     opted_out: (this.ph as any).has_opted_out_capturing?.(),
        //     consent_one: c,
        //     consent_two: this.consent,
        // });

    }

    /**
     * Capture event
     * @param eventName 
     * @param props 
     * @param kind 
     * @returns 
     */
    capture(eventName: string, props: Record<string, any> = {}, kind: EventKind = 'measurement'): void {

        const name = eventName?.trim() || 'event';

        // Rispetta le preferences
        if(
            (kind === 'functional'  && !this.consent.preferences) ||
            (kind === 'measurement' && !this.consent.statistics) ||
            (kind === 'marketing'   && !this.consent.marketing)
        ){
            
            return; // categoria non consentita → drop silenzioso

        }

        // Accoda se SDK non pronto
        if(!this.ready || !(this.ph as any).__loaded){

            this.queue.push({ name, props, kind });
            return;

        }

        if(this.isSafe()){
            
            const sid = this.getEphemeralSid();

            if((this.ph as any).has_opted_out_capturing?.()) (this.ph as any).opt_in_capturing?.();

            this.ph.capture(
                name,
                { sid, ...props },
                {
                    send_instantly: true,
                    // @ts-expect-error: proprietà runtime non tipizzata
                    disable_persistence: true,
                }
            );

        }else{

            this.ph.capture(name, props);
            
        }

    }

    /**
     * Contact Form
     * @param type_form
     * @param type_businesses
     * @param name 
     * @param surname 
     * @param email 
     * @param city 
     * @param phone 
     * @param message 
     */
    captureContactForm(
        type_form: string = '',
        type_businesses: string = '',
        name: string = '',
        surname: string = '',
        email: string = '',
        city: string = '',
        phone: string = '',
        message: string = '',
    ){

        // Get the session ID
        const sessionId = this.ph?.get_session_id();

        // Get the ephemeral session ID
        const sid = this.getEphemeralSid();

        // Identify user
        if(!this.isSafe()){

            this.ph.identify(email, {
                'name': name,
                'surname': surname,
                'email': email,
                'phone': phone,
            });
            
            // Caputre event
            this.capture(type_form, {
                '$session_id': !this.isSafe() ? sessionId : sid,
                'type_businesses': type_businesses,
                'name': name,
                'surname': surname,
                'email': email,
                'city': city,
                'phone': phone,
                'message': message,
            }, 'manual_anon');
            
        }else{

            this.capture(type_form, {
                '$session_id': !this.isSafe() ? sessionId : sid,
                'type_businesses': type_businesses,
            }, 'manual_anon');

        }

        // console.log('[PH captureContactForm]', {
        //     event: type_form,
        //     safe: this.isSafe(),
        //     session_id: sessionId,
        //     mode: this.ph?.config?.persistence,
        //     opted_out: this.ph?.has_opted_out_capturing?.(),
        //     loaded: this.ph?.__loaded,
        //     distinct_id: this.ph?.get_distinct_id?.()
        // });

    }

    /**
     * Capture Gift Card
     * @param gift_card 
     * @param gift_card_name 
     * @param gift_card_price 
     * @param name_recipient 
     * @param mail_recipient 
     * @param message_recipient 
     * @param name_sender 
     * @param mail_sender 
     */
    captureGiftCardPurchase(
        gift_card: string = '',
        gift_card_name: string = '',
        gift_card_price: string = '',
        name_recipient: string = '',
        mail_recipient: string = '',
        message_recipient: string = '',
        name_sender: string = '',
        mail_sender: string = '',
    ){
        
        // Get the session ID
        const sessionId = this.ph?.get_session_id();

        // Get the ephemeral session ID
        const sid = this.getEphemeralSid();

        // Identify user
        if(!this.isSafe()){

            this.ph.identify(mail_sender, {
                'name': name_sender,
                'email': mail_sender,
            });
            
            // Caputre event
            this.capture('Gift Card Purchase', {
                '$session_id': !this.isSafe() ? sessionId : sid,
                'gift_card_id': gift_card,
                'gift_card_name': gift_card_name,
                'gift_card_price': gift_card_price,
                'name_recipient': name_recipient,
                'mail_recipient': mail_recipient,
                'message_recipient': message_recipient,
                'name_sender': name_sender,
                'mail_sender': mail_sender,
            }, 'manual_anon');
            
        }else{

            this.capture('Gift Card Purchase', {
                '$session_id': !this.isSafe() ? sessionId : sid,
            }, 'manual_anon');

        }

    }

    /**
     * Capture Newsletter Form
     * @param name 
     * @param email 
     */
    captureNewsletterForm(
        name: string = '',
        email: string = '',
    ){
        
        // Get the session ID
        const sessionId = this.ph?.get_session_id();

        // Get the ephemeral session ID
        const sid = this.getEphemeralSid();

        // Identify user
        if(!this.isSafe()){

            this.ph.identify(email, {
                'name': name,
                'email': email,
            });
            
            // Caputre event
            this.capture('Subscribe Newsletter', {
                '$session_id': !this.isSafe() ? sessionId : sid,
                'name': name,
                'email': email,
            }, 'manual_anon');
            
        }else{

             this.capture('Subscribe Newsletter', {
                '$session_id': !this.isSafe() ? sessionId : sid,
            }, 'manual_anon');

        }
       

    }

    /**
     * Capture Signup Form
     * @param email 
     */
    captureSignupForm(
        email: string = '',
    ){
        
        // Get the session ID
        const sessionId = this.ph?.get_session_id();

        // Get the ephemeral session ID
        const sid = this.getEphemeralSid();

        // Identify user
        if(!this.isSafe()){

            this.ph.identify(email, {
                'email': email,
            });
            
            // Caputre event
            this.capture('Signup Form', {
                '$session_id': !this.isSafe() ? sessionId : sid,
                'email': email,
            }, 'manual_anon');
            
        }else{

            this.capture('Signup Form', {
                '$session_id': !this.isSafe() ? sessionId : sid,
            }, 'manual_anon');

        }

    }

    captureDownPartyPurchase(
        name: string,
        surname: string,
        email: string,
        phone: string,
        room_name: string,
        party_date: string,
        time_start: string,
        time_end: string,
    ){
        
        // Get the session ID
        const sessionId = this.ph?.get_session_id();

        // Get the ephemeral session ID
        const sid = this.getEphemeralSid();

        // Identify user
        if(!this.isSafe()){

            this.ph.identify(email, {
                'name': name,
                'surname': surname,
                'email': email,
                'phone': phone,
            });
            
            // Caputre event
            this.capture('Down Party Purchase', {
                '$session_id': !this.isSafe() ? sessionId : sid,
                'name': name,
                'surname': surname,
                'email': email,
                'phone': phone,
                'room_name': room_name,
                'party_date': party_date,
                'time_start': time_start,
                'time_end': time_end,
            }, 'manual_anon');
            
        }else{

            this.capture('Down Party Purchase', {
                '$session_id': !this.isSafe() ? sessionId : sid,
            }, 'manual_anon');

        }

    }

    /**
     * Capture Booking
     * @param name 
     * @param surname 
     * @param email 
     * @param phone 
     */
    captureBooking(
        type_form: string = '',
        name: string = '',
        surname: string = '',
        email: string = '',
        phone: string = '',
        price: string = '',
    ){
        
        price = price?.replace(/&nbsp;€/g, "");

        // Get the session ID
        const sessionId = this.ph?.get_session_id();

        // Get the ephemeral session ID
        const sid = this.getEphemeralSid();

        // Get the price
        const priceFinal = priceUtils.toCents(price);

        // Identify user
        if(!this.isSafe()){

            this.ph.identify(email, {
                'name': name,
                'surname': surname,
                'email': email,
                'phone': phone,
            });
            
            // Caputre event
            this.capture(type_form, {
                '$session_id': !this.isSafe() ? sessionId : sid,
                'name': name,
                'surname': surname,
                'email': email,
                'phone': phone,
                value: priceFinal,
                currency: 'EUR'
            }, 'manual_anon');
            
        }else{

            this.capture(type_form, {
                '$session_id': !this.isSafe() ? sessionId : sid,
                value: priceFinal,
                currency: 'EUR'
            }, 'manual_anon');

        }

    }

}